import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse an API timestamp for display. Stored timestamps are UTC but sometimes
 * arrive without a "Z"/offset; the browser would then parse them as local time,
 * skewing relative/absolute times (e.g. a just-created report looks hours old).
 * Treat timezone-less strings as UTC. No-op for values that already carry a tz.
 */
export function parseApiDate(value?: string | Date | null): Date {
  if (value instanceof Date) return value;
  if (!value) return new Date(NaN);
  const hasTimezone = /[zZ]|[+-]\d\d:?\d\d$/.test(value);
  return new Date(hasTimezone ? value : `${value}Z`);
}

export function emailContainsPlus(email: string) {
  const [localPart] = email.split("@");
  return localPart.includes("+");
}

// Sentinels written into target-criteria tag arrays by AI-assisted setup /
// migration flows. They must be scrubbed before measuring "is this section
// populated?" — otherwise an array of `["no information provided"]` passes
// a `length > 0` check and renders as an empty-looking chip.
export const PLACEHOLDER_TAG_VALUES = [
  "no information provided",
  "not applicable",
  "n/a",
  "none",
  "",
];

export function filterPlaceholderTagValues(
  values: string[] | undefined,
): string[] {
  if (!values) return [];
  return values.filter(
    (v) => !PLACEHOLDER_TAG_VALUES.includes(v.toLowerCase().trim()),
  );
}

// Compact relative-time formatter for dense data tables. Examples: "13d ago",
// "2h ago", "5m ago", "now". Single space between unit and "ago" keeps the
// rhythm consistent in mono-spaced columns where multi-word forms render with
// uneven gaps.
export const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInSeconds < 10) return "now";
  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays < 30) return `${diffInDays}d ago`;
  if (diffInMonths < 12) return `${diffInMonths}mo ago`;
  return `${diffInYears}y ago`;
};

// Workspace `opportunity_type` canonical list (SAAS-758). The BE accepts
// free-form strings so legacy records may carry short codes ("vc", "pe", …)
// or earlier draft long-form values; use `normalizeWorkspaceType` to render
// any stored value as one of these.
export const WorkspaceTypeOptions = [
  "Venture Capital",
  "Family Offices",
  "Corporate VC Fund",
  "Private Equity",
  "Accelerator",
  "Other",
] as const;

export type WorkspaceType = (typeof WorkspaceTypeOptions)[number];

const LEGACY_WORKSPACE_TYPE_MAP: Record<string, WorkspaceType> = {
  // Short codes from the original Settings page.
  vc: "Venture Capital",
  pe: "Private Equity",
  "family-office": "Family Offices",
  cvc: "Corporate VC Fund",
  accelerator: "Accelerator",
  "angel-group": "Other",
  other: "Other",
  // Long-form draft values from the prior inconsistent rollouts.
  "venture capital fund": "Venture Capital",
  "family office": "Family Offices",
  "corporate vc": "Corporate VC Fund",
  "angel groups": "Other",
  "pitch competitions": "Other",
  hackathon: "Other",
  "innovation challenges": "Other",
  incubator: "Other",
  grants: "Other",
  "debt financing": "Other",
  "not applicable": "Other",
};

export function normalizeWorkspaceType(
  value: string | null | undefined,
): WorkspaceType | "" {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  const canonical = WorkspaceTypeOptions.find(
    (opt) => opt.toLowerCase() === trimmed.toLowerCase(),
  );
  if (canonical) return canonical;

  return LEGACY_WORKSPACE_TYPE_MAP[trimmed.toLowerCase()] ?? "Other";
}

// Reverse-lookup for legacy deal records that still persist numeric stage codes.
const LEGACY_NUMERIC_STAGE_MAP: Record<number, string> = {
  0: "Pre-Seed",
  1: "Pre-Seed",
  1.1: "Pre-Seed",
  1.2: "Seed",
  2.1: "Series A",
  2.2: "Series B",
  2.3: "Series C",
  2.4: "Series C+",
  3: "IPO",
};

// AI-emitted "no value" sentinels that should not render as stage labels.
const STAGE_PLACEHOLDER_VALUES = new Set([
  "n/a",
  "na",
  "unknown",
  "not specified",
  "no information provided",
  "no information",
  "information not provided",
  "none",
]);

export function getStageText(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "Unknown";

  if (typeof value === "number") {
    return LEGACY_NUMERIC_STAGE_MAP[value] ?? "Unknown";
  }

  const trimmed = value.trim();
  if (!trimmed) return "Unknown";

  // Numeric strings ("1.1", " 2.3 ") fall back to the legacy map.
  const asNum = Number(trimmed);
  if (!isNaN(asNum)) {
    return LEGACY_NUMERIC_STAGE_MAP[asNum] ?? "Unknown";
  }

  if (STAGE_PLACEHOLDER_VALUES.has(trimmed.toLowerCase())) return "Unknown";

  const match = StageOptions.find(
    (opt) => opt.label.toLowerCase() === trimmed.toLowerCase()
  );
  return match ? match.label : trimmed;
}

export const getRiskGrade = (score: number | null): string | null => {
  if (!score) return null;

  if (score >= 10) return "aaa+";
  if (score >= 9.5) return "aaa";
  if (score >= 9) return "aa+";
  if (score >= 8.5) return "aa";
  if (score >= 8) return "aa-";
  if (score >= 7.5) return "bbb+";
  if (score >= 7) return "bbb";
  if (score >= 6.5) return "bbb-";
  if (score >= 6) return "bb+";
  if (score >= 5.5) return "bb";
  if (score >= 5) return "bb-";
  if (score >= 4.5) return "b+";
  if (score >= 4) return "b";
  if (score >= 3.5) return "b-";
  if (score >= 3.3) return "ccc+";
  if (score >= 3) return "ccc";
  if (score >= 2.5) return "ccc-";
  if (score >= 2) return "cc+";
  if (score >= 1.5) return "cc";
  if (score >= 1) return "c";
  if (score >= 0.5) return "c-";

  return "c-";
};

export const FILE_TYPE_MAPPING = {
  "Pitch Deck": "company pitchdeck",
  "Company Website": "website_content",
  "Company Logo": "company avatar",
  "Company Banner": "company background",
  "Financial Report": "company financial report",
  "Market Problem": "market problem",
  "Product/Service Deck": "product service deck",
  "Competitive Landscape": "competitive landscape",
  "Capitalization Table": "capitalization table",
  "Financial Details": "financial details",
  "Round Details": "round details",
  "Articles of Incorporation": "articles of incorporation",
  "Legal Councel - key contact information": "legal counsel contact info",
  "Patents, copyrights, licenses, trade secrets, and trademarks":
    "intellectual property",
  "Domain names": "domain names",
  "Founder Bio(s)": "founder bios",
  "Founder Employment Agreement(s)": "founder employment agreements",
  "Key Personnel Bios": "key personnel bios",
  "Key Personnel Employment Agreements": "key personnel employment agreements",
  "Organization Chart": "organization chart",
  "Business Advisory Board": "business advisory board",
  "Advisor Compensation": "advisor compensation",
  "Market Overview": "market overview",
  "Market Segmentation": "market segmentation",
  "Product Market Fit": "product market fit",
  "R&D": "research and development",
  "Products & Pipeline": "products and pipeline",
  "Business Model": "business model",
  "Customer Acquisition": "customer acquisition",
  "Major Customers and Partners": "major customers and partners",
  "Customer contracts/Service Agreements signed":
    "customer contracts service agreements",
  "Key Competitors and Competitive Advantage":
    "key competitors and competitive advantage",
  "Funding History": "funding history",
  "Non-Dilutive Funding": "non-dilutive funding",
  "Business Plan": "business plan",
  "Income Statement": "income_statement",
  "Balance Sheet": "balance_sheet",
  "Cash Flow Statement": "cash_flow_statement",
  "Profit and Loss Statement": "profit_and_loss_statement",
};

export const Industries = [
  "3D Printing",
  "Accounting",
  "Advertising",
  "Agriculture",
  "AI/ML",
  "Audiotech",
  "Blockchain",
  "Beauty/Cosmetics",
  "Cannabis",
  "Climate Tech",
  "Construction",
  "COVID-19",
  "Crypto",
  "Data",
  "Dev Ops",
  "Drone",
  "E-Commerce",
  "Education",
  "Employment",
  "Energy",
  "Enterprise Software",
  "Environmental",
  "Fashion",
  "Femtech",
  "Fintech",
  "Fitness",
  "Food",
  "Gaming",
  "Healthcare",
  "Hospitality",
  "IoT",
  "Insurtech",
  "Legal",
  "Lifestyle",
  "Manufacturing",
  "Marketing",
  "Media",
  "Music Tech",
  "Pet Tech",
  "Photography",
  "PropTech",
  "Real Estate",
  "Retail",
  "Robotics",
  "Security",
  "Social Media",
  "Space",
  "Sports",
  "Sports Tech",
  "Supply Chain/Logistics",
  "Telecom",
  "Transportation",
  "Travel",
  "Virtual Reality",
  "Web 3.0",
  "Wellness",
];

// Customer relationship models
export const BusinessModels = [
  { label: "Business to Business", value: "B2B", payer: "Business", receiver: "Business", notes: "Enterprise SaaS, wholesale, B2B services" },
  { label: "Business to Consumer", value: "B2C", payer: "Consumer", receiver: "Business", notes: "E-commerce, subscription apps, retail" },
  { label: "Business to Business to Consumer", value: "B2B2C", payer: "Consumer", receiver: "Business", notes: "Marketplace or SaaS resold to end consumer via another business" },
  { label: "Consumer to Consumer", value: "C2C", payer: "Consumer", receiver: "Consumer", notes: "Peer-to-peer marketplaces, second-hand goods" },
  { label: "Consumer to Business", value: "C2B", payer: "Business", receiver: "Consumer", notes: "Consumers sell value/data to businesses (influencers, reviews, data platforms)" },
  { label: "Direct to Consumer", value: "D2C", payer: "Consumer", receiver: "Business", notes: "Direct-to-consumer brands, bypassing retailers" },
  { label: "Business to Platform", value: "B2P", payer: "Business", receiver: "Platform", notes: "Merchants on Amazon, App Store fees" },
  { label: "Platform to Business", value: "P2B", payer: "Platform", receiver: "Business", notes: "Platforms paying merchants or partners" },
  { label: "Platform to Consumer", value: "P2C", payer: "Platform", receiver: "Consumer", notes: "Free apps with ads, rewards programs" },
  { label: "Consumer to Platform", value: "C2P", payer: "Consumer", receiver: "Platform", notes: "Consumers paying for platform fees or transactions" },
  { label: "Consumer to Consumer to Business", value: "C2C2B", payer: "Consumer", receiver: "Business", notes: "Consumers generate value ultimately monetized by business (UGC platforms, social media)" },
  { label: "Business to Government", value: "B2G", payer: "Government", receiver: "Business", notes: "Government contracts, procurement, enterprise software for public sector" },
  { label: "Government to Business", value: "G2B", payer: "Business", receiver: "Government", notes: "Licenses, taxes, compliance fees" },
  { label: "Government to Citizen", value: "G2C", payer: "Citizen", receiver: "Government", notes: "Public services, benefits, digital ID systems" },
  { label: "Business to Employee", value: "B2E", payer: "Business", receiver: "Employee", notes: "Employee benefits, internal SaaS, training platforms" },
  { label: "Business to Developer", value: "B2D", payer: "Business", receiver: "Developer", notes: "APIs, SDKs, developer tools" },
  { label: "Developer to Business", value: "D2B", payer: "Developer", receiver: "Business", notes: "Developers providing value/data back to business (plugins, apps)" },
  { label: "Developer to Developer", value: "D2D", payer: "Developer", receiver: "Developer", notes: "Developer-to-developer marketplaces, open-source monetization" },
  { label: "Machine to Machine", value: "M2M", payer: "Machine", receiver: "Machine", notes: "IoT, automated transactions, telemetry" },
  { label: "Business to Nonprofit", value: "B2N", payer: "Business", receiver: "Nonprofit", notes: "CSR platforms, donations, nonprofit SaaS" },
  { label: "Consumer to Nonprofit", value: "C2N", payer: "Consumer", receiver: "Nonprofit", notes: "Donations, fundraising platforms" },
];

// Revenue model patterns (61 models)
export const RevenueModels = [
  { label: "Add-on", value: "Add-on", description: "The core offering is priced competitively, but there are numerous extras that drive the final price up. In the end, the costumer pays more than he or she initially assumed. Customers benefit from a variable offer, which they can adapt to their specific needs.", examples: ["SAP", "Sega", "Salesforce", "Ryanair", "Amazon Web Services"] },
  { label: "Affiliation", value: "Affiliation", description: "The focus lies in supporting others to successfully sell products and directly benefit from successful transactions. Affiliates usually profit from some kind of pay-per-sale or pay-per-display compensation. The company, on the other hand, is able to gain access to a more diverse potential customer base without additional active sales or marketing efforts.", examples: ["Airbnb", "Dropbox", "American Express", "Amazon Store", "Best Buy"] },
  { label: "Aikido", value: "Aikido", description: "Aikido is a Japanese martial art in which the strength of an attacker is used against him or her. As a business model, Aikido allows a company to offer something diametrically opposed to the image and mindset of the competition. This new value proposition attracts customers who prefer ideas or concepts opposed to the mainstream.", examples: ["Nintendo", "Swatch"] },
  { label: "Auction", value: "Auction", description: "Auctioning means selling a product or service to the highest bidder. The final price is achieved when a particular end time of the auction is reached or when no higher offers are received. This allows the company to sell at the highest price acceptable to the customer.", examples: ["Amazon Web Services", "Lufthansa", "Google", "eBay"] },
  { label: "Barter", value: "Barter", description: "Barter is a method of exchange in which goods are given away to customers without the transaction of actual money. In return, they provide something of value to the sponsoring organisation.", examples: ["Pepsi", "Lufthansa", "Procter & Gamble"] },
  { label: "Cash Machine", value: "Cash Machine", description: "In the Cash Machine concept, the customer pays upfront for the products sold to the customer before the company is able to cover the associated expenses. This results in increased liquidity which can be used to amortise debt or to fund investments in other areas.", examples: ["Blacksocks", "Lufthansa", "American Express", "Groupon", "PayPal"] },
  { label: "Cross Selling", value: "Cross Selling", description: "In this model, services or products from a formerly excluded industry are added to the offerings, thus leveraging existing key skills and resources. Additional revenue can be generated with relatively few changes to the existing infrastructure and assets, since more potential customer needs are met.", examples: ["Shell", "Starbucks", "Tchibo", "IKEA", "American Airlines"] },
  { label: "Crowdfunding", value: "Crowdfunding", description: "A product, project or entire start-up is financed by a crowd of investors who wish to support the underlying idea, typically via the Internet. If the critical mass is achieved, the idea will be realized and investors receive special benefits, usually proportionate to the amount of money they provided.", examples: ["Pebble Technology", "Brainpool"] },
  { label: "Crowdsourcing", value: "Crowdsourcing", description: "The solution of a task or problem is adopted by an anonymous crowd, typically via the Internet. Contributors receive a small reward or have the chance to win a prize if their solution is chosen for production or sale.", examples: ["General Electric", "Procter & Gamble", "Cisco"] },
  { label: "Customer Loyalty", value: "Customer Loyalty", description: "Customers are retained and loyalty assured by providing value beyond the actual product or service itself, i.e., through incentive-based programs. The goal is to increase loyalty by creating an emotional connection or simply rewarding it with special offers.", examples: ["Marriott International", "Nestlé Nespresso", "The Home Depot", "American Express", "Airbnb"] },
  { label: "Digitization", value: "Digitization", description: "This pattern relies on the ability to turn existing products or services into digital variants, and thus offer advantages over tangible products, e.g., easier and faster distribution.", examples: ["Airbnb", "Amazon Store", "LinkedIn", "Napster", "Brainpool"] },
  { label: "Direct Selling", value: "Direct Selling", description: "Direct selling refers to a scenario whereby a company's products are not sold through intermediary channels, but are available directly from the manufacturer or service provider. In this way, the company skips the retail margin or any additional costs associated with the intermediates.", examples: ["Dollar Shave Club", "Carnegie Steel", "Harley Davidson", "Nestlé Nespresso", "Swatch"] },
  { label: "E-commerce", value: "E-commerce", description: "Traditional products or services are delivered through online channels only, thus removing costs associated with running a physical branch infrastructure. Customers benefit from higher availability and convenience.", examples: ["Denner", "IKEA", "Lego", "Flyeralarm", "Abril"] },
  { label: "Experience Selling", value: "Experience Selling", description: "The value of a product or service is increased with the customer experience offered with it. This opens the door for higher customer demand and commensurate increase in prices charged.", examples: ["Lamborghini", "Nestlé Nespresso", "Harley Davidson", "IKEA", "Freitag lab.ag"] },
  { label: "Flat Rate", value: "Flat Rate", description: "In this model, a single fixed fee for a product or service is charged, regardless of actual usage or time restrictions on it. The user benefits from a simple cost structure while the company benefits from a constant revenue stream.", examples: ["Verizon Communications", "Porsche", "Apple iPod/iTunes", "SBB", "LinkedIn"] },
  { label: "Fractional Ownership", value: "Fractional Ownership", description: "Fractional ownership describes the sharing of a certain asset class amongst a group of owners. Typically, the asset is capital intensive but only required on an occasional basis.", examples: ["Mobility Carsharing", "HomeBuy"] },
  { label: "Franchising", value: "Franchising", description: "The franchisor owns the brand name, products, and corporate identity, and these are licensed to independent franchisees who carry the risk of local operations. Revenue is generated as part of the franchisees' revenue and orders.", examples: ["Harley Davidson", "Subway", "Marriott International", "Denner", "Renault"] },
  { label: "Freemium", value: "Freemium", description: "The basic version of an offering is given away for free in the hope of eventually persuading the customers to pay for the premium version. The free offering is able to attract the highest volume of customers possible for the company.", examples: ["SlideShare", "Amazon Kindle", "Spotify", "Skype", "Hotmail"] },
  { label: "From Push-to-pull", value: "From Push-to-pull", description: "This pattern describes the strategy of a company to decentralize and thus add flexibility to the company's processes in order to be more customer focused. To quickly and flexibly respond to new customer needs, any part of the value chain including production or even research and development can be affected.", examples: ["Toyota", "Dell", "Zara"] },
  { label: "Guaranteed Availability", value: "Guaranteed Availability", description: "Within this model, the availability of a product or service is guaranteed, resulting in almost zero downtime. The customer can use the offering as required, which minimizes losses resulting from downtime.", examples: ["SAP", "Salesforce", "McFit", "Hilti", "Dropbox"] },
  { label: "Hidden Revenue", value: "Hidden Revenue", description: "The logic that the user is responsible for the income of the business is abandoned. Instead, the main source of revenue comes from a third party, which cross-finances whatever free or low-priced offering attracts the users.", examples: ["Ryanair", "Twitter", "Pinterest", "Google", "YouTube"] },
  { label: "Ingredient Branding", value: "Ingredient Branding", description: "Ingredient branding describes the specific selection of an ingredient, component, and brand originating from a specific supplier, which will be included in another product. This product is then additionally branded and advertised with the ingredient product, collectively adding value for the customer.", examples: ["Intel", "Microsoft", "Bosch"] },
  { label: "Integrator", value: "Integrator", description: "An integrator is in command of the bulk of the steps in a value-adding process. The control of all resources and capabilities in terms of value creation lies with the company. Efficiency gains, economies of scope, and lower dependencies from suppliers result in a decrease in costs.", examples: ["Amazon Store", "Standard Oil Company", "Lufthansa", "Carnegie Steel", "JCDecaux"] },
  { label: "Layer Player", value: "Layer Player", description: "A layer player is a specialized company limited to the provision of one value-adding step for different value chains. This step is typically offered within a variety of independent markets and industries.", examples: ["Foxconn", "Microsoft", "Intel", "Mozilla", "Max Havelaar"] },
  { label: "Leverage Customer Data", value: "Leverage Customer Data", description: "New value is created by collecting customer data and preparing it in beneficial ways for internal usage or interested third-parties. Revenues are generated by either selling this data directly to others or leveraging it for own purposes.", examples: ["Twitter", "Pinterest", "SlideShare", "Google", "Airbnb"] },
  { label: "License", value: "License", description: "Efforts are focused on developing intellectual property that can be licensed to other manufacturers. This model relies not on the realization and utilization of knowledge in the form of products, but attempts to transform these intangible goods into money.", examples: ["ARM", "General Electric", "IBM", "Brilliance China Auto", "Toyota"] },
  { label: "Lock-in", value: "Lock-in", description: "Customers are locked into a vendor's world of products and services. Using another vendor is impossible without incurring substantial switching costs, and thus protecting the company from losing customers.", examples: ["Lego", "Microsoft", "Gillette", "LinkedIn", "Salesforce"] },
  { label: "Long Tail", value: "Long Tail", description: "Instead of concentrating on blockbusters, the main bulk of revenues is generated through a 'long tail' of niche products. Individually, these neither demand high volumes, nor allow for a high margin.", examples: ["CDnow", "Facebook", "Valve Corporation", "Blockbuster", "Apple iPod/iTunes"] },
  { label: "Make More Of It", value: "Make More Of It", description: "Know-how and other available assets existing in the company are not only used to build own products, but also offered to other companies. Slack resources, therefore, can be used to create additional revenue besides those generated directly from the core value proposition.", examples: ["BASF", "Amazon Web Services"] },
  { label: "Mass Customization", value: "Mass Customization", description: "Customizing products through mass production once seemed to be an impossible endeavor. The approach of modular products and production systems has enabled the efficient individualization of products.", examples: ["Spotify", "Lego Factory", "IKEA", "Lego", "Dell"] },
  { label: "No Frills", value: "No Frills", description: "Value creation focuses on what is necessary to deliver the core value proposition of a product or service, typically as basic as possible. Cost savings are shared with the customer, usually resulting in a customer base with lower purchasing power or purchasing willingness.", examples: ["Aldi", "Ford", "Craigslist", "Aravind Eye Care System", "McDonald's"] },
  { label: "Open Business Model", value: "Open Business Model", description: "In open business models, collaboration with partners in the ecosystem becomes a central source of value creation. Companies pursuing an open business model actively search for novel ways of working together with suppliers, customers, or complementors.", examples: ["Valve Corporation", "Abril"] },
  { label: "Open Source", value: "Open Source", description: "In software engineering, the source code of a software product is not kept proprietary, but is freely accessible for anyone. Generally, this could be applied to any technology details of any product. Money is typically earned with services that are complimentary to the product, such as consulting and support.", examples: ["Mozilla", "Apple iPhone/AppStore", "Pebble Technology", "Wikipedia", "IBM"] },
  { label: "Orchestrator", value: "Orchestrator", description: "Within this model, the company's focus is on the core competencies in the value chain. The other value chain segments are outsourced and actively coordinated. This allows the company to reduce costs and benefit from the suppliers' economies of scale.", examples: ["Groupon", "Nike", "Richelieu Foods", "Procter & Gamble"] },
  { label: "Pay Per Use", value: "Pay Per Use", description: "In this model, the actual usage of a service or product is metered. The customer pays on the basis of what he or she effectively consumes. The company is able to attract customers who wish to benefit from the additional flexibility.", examples: ["Mobility Carsharing", "Car2Go", "Flyeralarm", "Amazon Web Services", "Google"] },
  { label: "Pay What You Want", value: "Pay What You Want", description: "The buyer pays any desired amount for a given commodity, sometimes even zero. In some cases, a minimum floor price may be set, and/or a suggested price may be indicated as guidance for the buyer.", examples: ["Panera Bread Bakery", "Humble Bundle"] },
  { label: "Peer-to-peer", value: "Peer-to-peer", description: "This model is based on a cooperation that specializes in mediating between individuals belonging to a homogeneous group. The company offers a meeting point, i.e., an online database and communication service that connects these individuals.", examples: ["Skype", "SlideShare", "Lego Factory", "Napster", "PayPal"] },
  { label: "Performance-based Contracting", value: "Performance-based Contracting", description: "A product's price is not based upon the physical value, but on the performance or valuable outcome it delivers in the form of a service. Performance based contractors are often strongly integrated into the value creation process of their customers.", examples: ["Amazon Web Services", "BASF", "Rolls-Royce", "General Electric", "Xerox"] },
  { label: "Razor And Blade", value: "Razor And Blade", description: "The basic product is cheap or given away for free. The consumables that are needed to use or operate it, on the other hand, are expensive and sold at high margins.", examples: ["Sega", "Apple iPhone/AppStore", "Nestlé Nespresso", "Amazon Kindle", "Gillette"] },
  { label: "Rent Instead Of Buy", value: "Rent Instead Of Buy", description: "The customer does not buy a product, but instead rents it. This lowers the capital typically needed to gain access to the product. The company itself benefits from higher profits on each product, as it is paid for the duration of the rental period.", examples: ["Hilti", "Blockbuster", "Xerox", "Netflix", "General Electric"] },
  { label: "Revenue Sharing", value: "Revenue Sharing", description: "Revenue sharing refers to firms' practice of sharing revenues with their stakeholders, such as complementors or even rivals. Thus, in this business model, advantageous properties are merged to create symbiotic effects in which additional profits are shared with partners.", examples: ["Apple iPhone/AppStore", "Apple iPod/iTunes", "Groupon", "Google", "Amazon Kindle"] },
  { label: "Reverse Engineering", value: "Reverse Engineering", description: "This pattern refers to obtaining a competitor's product, taking it apart, and using this information to produce a similar or compatible product. Because no huge investment in research or development is necessary, these products can be offered at a lower price.", examples: ["Denner", "Bayer", "Brilliance China Auto"] },
  { label: "Reverse Innovation", value: "Reverse Innovation", description: "Simple and inexpensive products, that were developed within and for emerging markets, are also sold in industrial countries. The term 'reverse' refers to the process by which new products are typically developed in industrial countries and then adapted to fit emerging market needs.", examples: ["Logitech", "Procter & Gamble", "General Electric", "Renault", "Hindustan Unilever"] },
  { label: "Robin Hood", value: "Robin Hood", description: "The same product or service is provided to 'the rich' at a much higher price than to 'the poor'. Thus, the main bulk of profits are generated from the wealthy customer base. Serving 'the poor' is not profitable per se, but creates economies of scale.", examples: ["Warby Parker", "Aravind Eye Care System"] },
  { label: "Self-service", value: "Self-service", description: "A part of the value creation is transferred to the customer in exchange for a lower price of the service or product. This is particularly suited for process steps that add relatively little perceived value for the customer, but incur high costs.", examples: ["CEWE Color", "IKEA", "Car2Go", "Subway", "Flyeralarm"] },
  { label: "Shop-in-shop", value: "Shop-in-shop", description: "Instead of opening new branches, a partner is chosen whose branches can profit from integrating the company's offerings in a way that imitates a small shop within another shop (a win-win situation).", examples: ["Nestlé Nespresso", "Levi's", "Tchibo", "Deutsche Post", "Bosch"] },
  { label: "Solution Provider", value: "Solution Provider", description: "A full service provider offers total coverage of products and services in a particular domain, consolidated via a single point of contact. Special know-how is given to the customer in order to increase his or her efficiency and performance.", examples: ["IBM", "Deutsche Post", "Tetra Pak", "Amazon Web Services", "Google"] },
  { label: "Subscription", value: "Subscription", description: "The customer pays a regular fee, typically on a monthly or an annual basis, in order to gain access to a product or service. While customers mostly benefit from lower usage costs and general service availability, the company generates a more steady income stream.", examples: ["Napster", "Blacksocks", "Dropbox", "Salesforce", "Spotify"] },
  { label: "Supermarket", value: "Supermarket", description: "A company sells a large variety of readily available products and accessories under one roof. Generally, the assortment of products is large but the prices are kept low. More customers are attracted due to the great range on offer.", examples: ["IKEA", "Amazon Store", "Best Buy", "H&M"] },
  { label: "Target The Poor", value: "Target The Poor", description: "The product or service offering does not target the premium customer, but rather, the customer positioned at the base of the pyramid. Customers with lower purchasing power benefit from affordable products.", examples: ["H&M", "HomeBuy", "McDonald's", "Renault", "Procter & Gamble"] },
  { label: "Trash-to-cash", value: "Trash-to-cash", description: "Used products are collected and either sold in other parts of the world or transformed into new products. The profit scheme is essentially based on low-to-no purchase prices.", examples: ["Freitag lab.ag"] },
  { label: "Two-sided Market", value: "Two-sided Market", description: "A two-sided market facilitates interactions between multiple interdependent groups of customers. The value of the platform increases as more groups or as more individual members of each group are using it.", examples: ["Amazon Web Services", "JCDecaux", "Amazon Kindle", "Google", "Salesforce"] },
  { label: "Ultimate Luxury", value: "Ultimate Luxury", description: "This pattern describes the strategy of a company to focus on the upper side of society's pyramid. This allows a company to distinguish its products or services greatly from others. High standards of quality or exclusive privileges are the main focus.", examples: ["American Airlines", "Porsche", "Marriott International", "Lamborghini", "Abbot Downing"] },
  { label: "User Designed", value: "User Designed", description: "Within user manufacturing, a customer is both the manufacturer and the consumer. An online platform provides the customer with the necessary support in order to design and merchandise the product.", examples: ["CEWE Color", "Apple iPhone/AppStore", "YouTube", "Amazon Kindle", "Wikipedia"] },
  { label: "Whitelabel", value: "Whitelabel", description: "A white label producer allows other companies to distribute its goods under their brands, so that it appears as if they are made by them. The same product or service is often sold by multiple marketers and under different brands.", examples: ["Google", "Richelieu Foods", "Foxconn", "Amazon Web Services", "CEWE Color"] },
  { label: "Sensor As A Service", value: "Sensor As A Service", description: "The use of sensors permits additional services for physical offerings, or wholly new independent services. It is not the sensor that generates the primary revenue, but the analysis of the data that the sensor creates.", examples: ["Procter & Gamble", "Panasonic and Allianz Assist", "Google Nest", "Somfy", "Streetline"] },
  { label: "Virtualization", value: "Virtualization", description: "This pattern describes the imitation of a traditionally physical process in a virtual environment, e.g., a virtual workspace. The advantage for the customer is the ability to interact with the process from any location or device.", examples: ["Dropbox", "Amazon Web Services", "Bertelsmann - Udacity", "Microsoft Teams", "DUFL"] },
  { label: "Object Self-service", value: "Object Self-service", description: "Through the use of sensors and inclusion in an IT structure, an object can generate orders by itself. This makes fully-automated processes such as replenishment possible and increases the speed of interaction with the object.", examples: ["Würth iBin", "FELFEL", "HP Instant Ink", "Miele WMH 721 WPS EditionConn@ct"] },
  { label: "Object As Point-of-sale", value: "Object As Point-of-sale", description: "The point of sale of consumables moves to the point of consumption. This generates a stronger lock-in and results in higher customer retention.", examples: ["Google Glasses", "Amazon Alexa", "Ubitricity", "Bird", "Amazon - Dash Concept"] },
  { label: "Prosumer", value: "Prosumer", description: "Companies enable customers to become producers themselves. The customer is integrated into the value chain and can profit from the resulting product, while the company has fewer investment costs for production and overheads.", examples: ["Amazon Managed Blockchain", "Easy Smart Grid", "Instagram", "YouTube"] },
];

export const countries = [
  "Antigua and Barbuda",
  "Argentina",
  "Aruba",
  "Austria",
  "Australia",
  "Bahamas",
  "Bangladesh",
  "Barbados",
  "Belize",
  "Brazil",
  "British Virgin Islands",
  "Belgium",
  "Cameroon",
  "Canada",
  "Cayman Islands",
  "Chile",
  "China",
  "Croatia",
  "Colombia",
  "Costa Rica",
  "Cuba",
  "Curacao",
  "Denmark",
  "Dominica",
  "Dominican Republic",
  "Egypt",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hong Kong",
  "India",
  "Indonesia",
  "Iran",
  "Ireland",
  "Israel",
  "Italy",
  "Japan",
  "Jamaica",
  "Kenya",
  "Lithuania",
  "Malaysia",
  "Martinique",
  "Mexico",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Nigeria",
  "Norway",
  "Pakistan",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Romania",
  "Russia",
  "Saudi Arabia",
  "Saint Kitts and Nevis",
  "Singapore",
  "South Africa",
  "South Korea",
  "Spain",
  "Sri Lanka",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Thailand",
  "Trinidad & Tobago",
  "Turkey",
  "Turks and Caicos Islands",
  "UAE",
  "Uganda",
  "Ukraine",
  "United States",
  "United Kingdom",
  "Uruguay",
  "US Virgin Islands",
  "Venezula",
  "Vietnam",
];

export const StageOptions = [
  { value: "Pre-Seed",  label: "Pre-Seed"  },
  { value: "Seed",      label: "Seed"      },
  { value: "Series A",  label: "Series A"  },
  { value: "Series B",  label: "Series B"  },
  { value: "Series C",  label: "Series C"  },
  { value: "Series C+", label: "Series C+" },
  { value: "IPO",       label: "IPO"       },
];

// GICS (Global Industry Classification Standard) — 2-level structure
// Industry (8-digit) → Sub-Industry (10-digit, from level_5_map)
import gicsLevel5Data from "./gics-level5.json";

export const GICSIndustries = [
  { code: "10101010", name: "Oil & Gas Drilling" },
  { code: "10101020", name: "Oil & Gas Equipment & Services" },
  { code: "10102010", name: "Integrated Oil & Gas" },
  { code: "10102020", name: "Oil & Gas Exploration & Production" },
  { code: "10102030", name: "Oil & Gas Refining & Marketing" },
  { code: "10102040", name: "Oil & Gas Storage & Transportation" },
  { code: "10102050", name: "Coal & Consumable Fuels" },
  { code: "15101010", name: "Commodity Chemicals" },
  { code: "15101020", name: "Diversified Chemicals" },
  { code: "15101030", name: "Fertilizers & Agricultural Chemicals" },
  { code: "15101040", name: "Industrial Gases" },
  { code: "15101050", name: "Specialty Chemicals" },
  { code: "15102010", name: "Construction Materials" },
  { code: "15103010", name: "Metal, Glass & Plastic Containers" },
  { code: "15103020", name: "Paper & Plastic Packaging Products & Materials" },
  { code: "15104010", name: "Aluminum" },
  { code: "15104020", name: "Diversified Metals & Mining" },
  { code: "15104025", name: "Copper" },
  { code: "15104030", name: "Gold" },
  { code: "15104040", name: "Precious Metals & Minerals" },
  { code: "15104045", name: "Silver" },
  { code: "15104050", name: "Steel" },
  { code: "15105010", name: "Forest Products" },
  { code: "15105020", name: "Paper Products" },
  { code: "20101010", name: "Aerospace & Defense" },
  { code: "20102010", name: "Building Products" },
  { code: "20103010", name: "Construction & Engineering" },
  { code: "20104010", name: "Electrical Components & Equipment" },
  { code: "20104020", name: "Heavy Electrical Equipment" },
  { code: "20105010", name: "Industrial Conglomerates" },
  { code: "20106010", name: "Construction Machinery & Heavy Transportation Equipment" },
  { code: "20106015", name: "Agricultural & Farm Machinery" },
  { code: "20106020", name: "Industrial Machinery & Supplies & Components" },
  { code: "20107010", name: "Trading Companies & Distributors" },
  { code: "20201010", name: "Commercial Printing" },
  { code: "20201050", name: "Environmental & Facilities Services" },
  { code: "20201060", name: "Office Services & Supplies" },
  { code: "20201070", name: "Diversified Support Services" },
  { code: "20201080", name: "Security & Alarm Services" },
  { code: "20202010", name: "Human Resource & Employment Services" },
  { code: "20202020", name: "Research & Consulting Services" },
  { code: "20202030", name: "Data Processing & Outsourced Services" },
  { code: "20301010", name: "Air Freight & Logistics" },
  { code: "20302010", name: "Passenger Airlines" },
  { code: "20303010", name: "Marine Transportation" },
  { code: "20304010", name: "Rail Transportation" },
  { code: "20304030", name: "Cargo Ground Transportation" },
  { code: "20304040", name: "Passenger Ground Transportation" },
  { code: "20305010", name: "Airport Services" },
  { code: "20305020", name: "Highways & Railtracks" },
  { code: "20305030", name: "Marine Ports & Services" },
  { code: "25101010", name: "Automotive Parts & Equipment" },
  { code: "25101020", name: "Tires & Rubber" },
  { code: "25102010", name: "Automobile Manufacturers" },
  { code: "25102020", name: "Motorcycle Manufacturers" },
  { code: "25201010", name: "Consumer Electronics" },
  { code: "25201020", name: "Home Furnishings" },
  { code: "25201030", name: "Homebuilding" },
  { code: "25201040", name: "Household Appliances" },
  { code: "25201050", name: "Housewares & Specialties" },
  { code: "25202010", name: "Leisure Products" },
  { code: "25203010", name: "Apparel, Accessories & Luxury Goods" },
  { code: "25203020", name: "Footwear" },
  { code: "25203030", name: "Textiles" },
  { code: "25301010", name: "Casinos & Gaming" },
  { code: "25301020", name: "Hotels, Resorts & Cruise Lines" },
  { code: "25301030", name: "Leisure Facilities" },
  { code: "25301040", name: "Restaurants" },
  { code: "25302010", name: "Education Services" },
  { code: "25302020", name: "Specialized Consumer Services" },
  { code: "25501010", name: "Distributors" },
  { code: "25503030", name: "Broadline Retail" },
  { code: "25504010", name: "Apparel Retail" },
  { code: "25504020", name: "Computer & Electronics Retail" },
  { code: "25504030", name: "Home Improvement Retail" },
  { code: "25504040", name: "Other Specialty Retail" },
  { code: "25504050", name: "Automotive Retail" },
  { code: "25504060", name: "Homefurnishing Retail" },
  { code: "30101010", name: "Drug Retail" },
  { code: "30101020", name: "Food Distributors" },
  { code: "30101030", name: "Food Retail" },
  { code: "30101040", name: "Consumer Staples Merchandise Retail" },
  { code: "30201010", name: "Brewers" },
  { code: "30201020", name: "Distillers & Vintners" },
  { code: "30201030", name: "Soft Drinks & Non-alcoholic Beverages" },
  { code: "30202010", name: "Agricultural Products & Services" },
  { code: "30202030", name: "Packaged Foods & Meats" },
  { code: "30203010", name: "Tobacco" },
  { code: "30301010", name: "Household Products" },
  { code: "30302010", name: "Personal Care Products" },
  { code: "35101010", name: "Health Care Equipment" },
  { code: "35101020", name: "Health Care Supplies" },
  { code: "35102010", name: "Health Care Distributors" },
  { code: "35102015", name: "Health Care Services" },
  { code: "35102020", name: "Health Care Facilities" },
  { code: "35102030", name: "Managed Health Care" },
  { code: "35103010", name: "Health Care Technology" },
  { code: "35201010", name: "Biotechnology" },
  { code: "35202010", name: "Pharmaceuticals" },
  { code: "35203010", name: "Life Sciences Tools & Services" },
  { code: "40101010", name: "Diversified Banks" },
  { code: "40101015", name: "Regional Banks" },
  { code: "40201020", name: "Diversified Financial Services" },
  { code: "40201030", name: "Multi-Sector Holdings" },
  { code: "40201040", name: "Specialized Finance" },
  { code: "40201050", name: "Commercial & Residential Mortgage Finance" },
  { code: "40201060", name: "Transaction & Payment Processing Services" },
  { code: "40202010", name: "Consumer Finance" },
  { code: "40203010", name: "Asset Management & Custody Banks" },
  { code: "40203020", name: "Investment Banking & Brokerage" },
  { code: "40203030", name: "Diversified Capital Markets" },
  { code: "40203040", name: "Financial Exchanges & Data" },
  { code: "40204010", name: "Mortgage REITs" },
  { code: "40301010", name: "Insurance Brokers" },
  { code: "40301020", name: "Life & Health Insurance" },
  { code: "40301030", name: "Multi-line Insurance" },
  { code: "40301040", name: "Property & Casualty Insurance" },
  { code: "40301050", name: "Reinsurance" },
  { code: "45102010", name: "IT Consulting & Other Services" },
  { code: "45102030", name: "Internet Services & Infrastructure" },
  { code: "45103010", name: "Application Software" },
  { code: "45103020", name: "Systems Software" },
  { code: "45201020", name: "Communications Equipment" },
  { code: "45202030", name: "Technology Hardware, Storage & Peripherals" },
  { code: "45203010", name: "Electronic Equipment & Instruments" },
  { code: "45203015", name: "Electronic Components" },
  { code: "45203020", name: "Electronic Manufacturing Services" },
  { code: "45203030", name: "Technology Distributors" },
  { code: "45301010", name: "Semiconductor Materials & Equipment" },
  { code: "45301020", name: "Semiconductors" },
  { code: "50101010", name: "Alternative Carriers" },
  { code: "50101020", name: "Integrated Telecommunication Services" },
  { code: "50102010", name: "Wireless Telecommunication Services" },
  { code: "50201010", name: "Advertising" },
  { code: "50201020", name: "Broadcasting" },
  { code: "50201030", name: "Cable & Satellite" },
  { code: "50201040", name: "Publishing" },
  { code: "50202010", name: "Movies & Entertainment" },
  { code: "50202020", name: "Interactive Home Entertainment" },
  { code: "50203010", name: "Interactive Media & Services" },
  { code: "55101010", name: "Electric Utilities" },
  { code: "55102010", name: "Gas Utilities" },
  { code: "55103010", name: "Multi-Utilities" },
  { code: "55104010", name: "Water Utilities" },
  { code: "55105010", name: "Independent Power Producers & Energy Traders" },
  { code: "55105020", name: "Renewable Electricity" },
  { code: "60101010", name: "Diversified REITs" },
  { code: "60102510", name: "Industrial REITs" },
  { code: "60103010", name: "Hotel & Resort REITs" },
  { code: "60104010", name: "Office REITs" },
  { code: "60105010", name: "Health Care REITs" },
  { code: "60106010", name: "Multi-Family Residential REITs" },
  { code: "60106020", name: "Single-Family Residential REITs" },
  { code: "60107010", name: "Retail REITs" },
  { code: "60108010", name: "Other Specialized REITs" },
  { code: "60108020", name: "Self-Storage REITs" },
  { code: "60108030", name: "Telecom Tower REITs" },
  { code: "60108040", name: "Timber REITs" },
  { code: "60108050", name: "Data Center REITs" },
  { code: "60201010", name: "Diversified Real Estate Activities" },
  { code: "60201020", name: "Real Estate Operating Companies" },
  { code: "60201030", name: "Real Estate Development" },
  { code: "60201040", name: "Real Estate Services" },
];

export const GICSSubIndustries = Object.entries(
  gicsLevel5Data as Record<string, { sub_industry_name: string; sub_industry_description: string }>
).map(([code, data]) => ({
  code,
  name: data.sub_industry_name,
  description: data.sub_industry_description,
}));

// Helper: get sub-industries for a given industry (8-digit) code
export const getSubIndustriesByIndustry = (industryCode: string) => {
  return GICSSubIndustries.filter((si) => si.code.startsWith(industryCode));
};

// Get parent industry code from a sub-industry (10-digit) code
export const getGICSParentCodes = (code: string) => {
  return {
    industryCode: code.substring(0, 8),
    subIndustryCode: code.length === 10 ? code : undefined,
  };
};

// Get names from codes
export const getGICSNames = (codes: {
  industryCode?: string;
  subIndustryCode?: string;
}) => {
  return {
    industryName: GICSIndustries.find((i) => i.code === codes.industryCode)?.name,
    subIndustryName: GICSSubIndustries.find((si) => si.code === codes.subIndustryCode)?.name,
  };
};

// Resolve a single industry entry — handles raw GICS codes (all digits, e.g.
// "451020", "20202020") AND already-resolved human labels (e.g. "Enterprise
// Software"). Returns the human label when a match exists, the original
// string otherwise. Use this anywhere we render `deal.overview.industry[]`
// so data quality issues don't leak as raw codes in the UI.
export function resolveIndustryLabel(value: string): string {
  if (!value) return value;
  const trimmed = value.trim();
  // Codes are 4-10 digit numeric strings (GICS spec); skip the regex check
  // for anything that contains letters.
  if (!/^\d{4,10}$/.test(trimmed)) return value;
  const sub = GICSSubIndustries.find((si) => si.code === trimmed);
  if (sub) return sub.name;
  const ind = GICSIndustries.find((i) => i.code === trimmed);
  if (ind) return ind.name;
  // Unknown code — return em-dash rather than the bare digits, which would
  // read as a broken cell to an analyst.
  return "—";
}

// Get display name for any GICS code (auto-detects level by code length)
export const getGICSDisplayName = (code: string): string => {
  switch (code.length) {
    case 8:
      return GICSIndustries.find((i) => i.code === code)?.name || code;
    case 10:
      return GICSSubIndustries.find((si) => si.code === code)?.name || code;
    default:
      return code;
  }
};

export const CountryOptions = countries.map((country) => ({
  value: country,
  label: country,
}));

export const IndustryOptions = Industries.map((industry) => ({
  value: industry,
  label: industry,
}));

// Field mapping for deal info API
export const DEAL_FIELD_MAPPING = {
  "Business Name": "company_name",
  Website: "company_website",
  LinkedIn: "linkedIn",
  Location: "address",
  Country: "address",
  Description: "short_bio",
  "Business Model": "business_model",
  "Revenue Model": "revenue_models",
  Industries: "industries",
  "Company Stage": "stage",
  Fundraising: "fundraising",
  "GICS Industry": "industries",
  "GICS Sub-Industry": "sub_industries",
} as const;

// Reverse mapping for converting API fields to display names
export const REVERSE_DEAL_FIELD_MAPPING = Object.entries(
  DEAL_FIELD_MAPPING
).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [value]: key,
  }),
  {} as Record<string, string>
);

export const DistinctionOptions = [
  {
    value: "Not Applicable",
    text: "Not Applicable",
  },
  {
    value: "Other",
    text: "Other",
  },
  {
    value: "disabled",
    text: "Founders of Disability",
  },
  {
    value: "veteran",
    text: "Veteran Founders",
  },
  {
    value: "lgbtq",
    text: "LGBTQ+ Founders",
  },
  {
    value: "minority",
    text: "Visible Minority Founder",
  },
  {
    value: "aboriginal",
    text: "Aboriginal Founders",
  },
  {
    value: "female_founder",
    text: "Female Founders",
  },
];

// Source type mapping for display names
// Includes mappings for source types and file types
// Keys are normalized to lowercase for case-insensitive matching
export const SOURCE_TYPE_MAPPING: Record<string, string> = {
  // Source type mappings
  website_summary: "Website Analysis",
  deal_info: "Deal Info",
  deal_notes: "Deal Notes",
  market_research: "Market Research",
  linkedin_company: "LinkedIn Profile",
  linkedin_company_scrape: "LinkedIn Profile",
  application_info: "Application Details",
  // File type mappings (reverse of FILE_TYPE_MAPPING, normalized to lowercase)
  "company pitchdeck": "Pitch Deck",
  website_content: "Company Website",
  "company avatar": "Company Logo",
  "company background": "Company Banner",
  "company financial report": "Financial Report",
  "market problem": "Market Problem",
  "product service deck": "Product/Service Deck",
  "competitive landscape": "Competitive Landscape",
  "capitalization table": "Capitalization Table",
  "financial details": "Financial Details",
  "round details": "Round Details",
  "articles of incorporation": "Articles of Incorporation",
  "legal counsel contact info": "Legal Councel - key contact information",
  "intellectual property":
    "Patents, copyrights, licenses, trade secrets, and trademarks",
  "domain names": "Domain names",
  "founder bios": "Founder Bio(s)",
  "founder employment agreements": "Founder Employment Agreement(s)",
  "key personnel bios": "Key Personnel Bios",
  "key personnel employment agreements": "Key Personnel Employment Agreements",
  "organization chart": "Organization Chart",
  "business advisory board": "Business Advisory Board",
  "advisor compensation": "Advisor Compensation",
  "market overview": "Market Overview",
  "market segmentation": "Market Segmentation",
  "product market fit": "Product Market Fit",
  "research and development": "R&D",
  "products and pipeline": "Products & Pipeline",
  "business model": "Business Model",
  "customer acquisition": "Customer Acquisition",
  "major customers and partners": "Major Customers and Partners",
  "customer contracts service agreements":
    "Customer contracts/Service Agreements signed",
  "key competitors and competitive advantage":
    "Key Competitors and Competitive Advantage",
  "funding history": "Funding History",
  "non-dilutive funding": "Non-Dilutive Funding",
  "business plan": "Business Plan",
  income_statement: "Income Statement",
  balance_sheet: "Balance Sheet",
  cash_flow_statement: "Cash Flow Statement",
  profit_and_loss_statement: "Profit and Loss Statement",
};

/**
 * Get the display name for a source type
 * @param sourceType - The source type key (e.g., "website_summary", "deal_info")
 * @returns The display name or a formatted version of the source type
 */
export function getNextStage(
  currentStatus: string,
  statuses: Array<{ title: string; order: number }>
): string | null {
  if (!statuses || statuses.length === 0) {
    return null;
  }

  // Find current status
  const currentStatusObj = statuses.find((s) => s.title === currentStatus);
  if (!currentStatusObj) {
    return null;
  }

  // Only move from first stage (order === 1) to second stage (order === 2)
  // If already in second stage or later, don't move
  if (currentStatusObj.order === 1) {
    const secondStage = statuses.find((s) => s.order === 2);
    return secondStage?.title || null;
  }

  // If not in first stage, return null (don't move)
  return null;
}

export function getSourceTypeDisplayName(sourceType: string): string {
  if (!sourceType) return "Unknown";

  const normalizedType = sourceType.toLowerCase();

  // Check exact match first
  if (SOURCE_TYPE_MAPPING[normalizedType]) {
    return SOURCE_TYPE_MAPPING[normalizedType];
  }

  // Check for partial matches (e.g., "linkedin_company_scrape" should match "linkedin_company")
  for (const [key, value] of Object.entries(SOURCE_TYPE_MAPPING)) {
    if (normalizedType.includes(key) || key.includes(normalizedType)) {
      return value;
    }
  }

  // Fallback: format the source type (e.g., "website_summary" -> "Website Summary")
  return sourceType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// types/tasks.ts
export interface TaskDefinition {
  displayName: string;
  description: string;
  icon: string;
  order: number;
  optional?: boolean;
  nextTask?: string;
  credits: number;
  url: string;
}

// constants/tasks.ts
export const TASKS: Record<string, TaskDefinition> = {
  add_deal: {
    displayName: "Add Deal",
    description: "Add a new deal to the pipeline",
    icon: "PlusCircle",
    order: 1,
    nextTask: "add_document",
    credits: 10,
    url: "/deals",
  },
  add_document: {
    displayName: "Add Document",
    description: "Upload relevant documents",
    icon: "File",
    order: 2,
    nextTask: "analyze_website",
    credits: 10,
    url: "/deals",
  },
  analyze_website: {
    displayName: "Analyze Website",
    description: "Perform website analysis",
    icon: "Globe",
    order: 3,
    nextTask: "deal_info",
    credits: 10,
    url: "/deals",
  },
  deal_info: {
    displayName: "Deal Info",
    description: "Provide detailed deal information",
    icon: "Info",
    order: 4,
    nextTask: "add_team_member",
    credits: 10,
    url: "/deals",
  },
  add_team_member: {
    displayName: "Add Team Member",
    description: "Add team members to the deal",
    icon: "Users",
    order: 5,
    optional: true,
    credits: 10,
    url: "/team",
  },
  startup_score: {
    displayName: "Business Risk",
    description: "Generate business risk",
    icon: "Trophy",
    order: 6,
    optional: true,
    credits: 10,
    url: "/deals",
  },
};
